interface TableRow {
  frequency: number | '';
  magnitude: number | '';
  phase: number | '';
}

interface ImpedanceTableProps {
  rows: TableRow[];
  onRowsChange: (rows: TableRow[]) => void;
  title: string;
}

export function ImpedanceTable({ rows, onRowsChange, title }: ImpedanceTableProps) {
  const handleRowChange = (index: number, field: keyof TableRow, value: string) => {
    const newRows = [...rows];
    const numValue = value === '' ? '' : parseFloat(value);
    
    if (numValue !== '' && isNaN(numValue as number)) {
      return; // Ignore non-numeric input
    }
    
    newRows[index] = { ...newRows[index], [field]: numValue };
    onRowsChange(newRows);
  };

  const handleDeleteRow = (index: number) => {
    const newRows = rows.filter((_, i) => i !== index);
    onRowsChange(newRows);
  };

  const handleAddRow = () => {
    onRowsChange([...rows, { frequency: '', magnitude: '', phase: '' }]);
  };

  const isRowValid = (row: TableRow) => {
    return (
      (row.frequency === '' || !isNaN(row.frequency)) &&
      (row.magnitude === '' || !isNaN(row.magnitude)) &&
      (row.phase === '' || !isNaN(row.phase))
    );
  };

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h4>
      
      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">
                Frequency (Hz)
              </th>
              <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">
                Magnitude (Ohm)
              </th>
              <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">
                Phase (rad)
              </th>
              <th className="px-4 py-3 text-center font-semibold text-gray-900 dark:text-white w-12">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                  No data points. Click "Add Row" to start.
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr
                  key={index}
                  className={`border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                    !isRowValid(row) ? 'bg-red-50 dark:bg-red-900 dark:bg-opacity-20' : ''
                  }`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      value={row.frequency}
                      onChange={(e) => handleRowChange(index, 'frequency', e.target.value)}
                      className={`w-full px-2 py-1 border rounded text-sm font-mono bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ${
                        row.frequency === '' || !isNaN(row.frequency as number)
                          ? 'border-gray-300 dark:border-gray-600'
                          : 'border-red-400'
                      }`}
                      placeholder="e.g., 10"
                      step="any"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      value={row.magnitude}
                      onChange={(e) => handleRowChange(index, 'magnitude', e.target.value)}
                      className={`w-full px-2 py-1 border rounded text-sm font-mono bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ${
                        row.magnitude === '' || !isNaN(row.magnitude as number)
                          ? 'border-gray-300 dark:border-gray-600'
                          : 'border-red-400'
                      }`}
                      placeholder="e.g., 97428.7"
                      step="any"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      value={row.phase}
                      onChange={(e) => handleRowChange(index, 'phase', e.target.value)}
                      className={`w-full px-2 py-1 border rounded text-sm font-mono bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ${
                        row.phase === '' || !isNaN(row.phase as number)
                          ? 'border-gray-300 dark:border-gray-600'
                          : 'border-red-400'
                      }`}
                      placeholder="e.g., 0.054"
                      step="any"
                    />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleDeleteRow(index)}
                      className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium"
                      title="Delete row"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <button
        onClick={handleAddRow}
        className="px-3 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
      >
        ➕ Add Row
      </button>
    </div>
  );
}
